import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CoachDeepDive, DeepDive, DeepDiveCard, HorizontalMenuTabs } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CATEGORY_TABS } from '@backrs_ui/grow-ui/src/pages/DeepDives/deepDives.constants';

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material';

const COACH_PROFILE_STRINGS = appStrings.modules.profile.public.coachProfile;

type CoachDeepDivesSectionProps = Readonly<{
  deepDives: CoachDeepDive[];
  onScheduleConsultation?: (contextProductId: number, contextName: string) => void;
}>;

const mapToDeepDive = (cd: CoachDeepDive): DeepDive => ({
  id: cd.id,
  product_id: cd.product_id,
  name: cd.name,
  description: cd.description,
  nugget_description: cd.description,
  is_popular: false,
  tags: cd.tags,
  code: cd?.code,
  media: cd.media,
});

export const CoachDeepDivesSection = ({
  deepDives,
  onScheduleConsultation,
}: CoachDeepDivesSectionProps) => {
  const navigate = useNavigate();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const selectedCategory = CATEGORY_TABS[selectedTabIndex]?.value || 'all';

  const filteredDeepDives = useMemo(() => {
    if (selectedCategory === 'all') {
      return deepDives;
    }

    return deepDives.filter((dd) => dd.tags?.some((tag) => tag.code === selectedCategory));
  }, [deepDives, selectedCategory]);

  const mappedDeepDives = useMemo(() => filteredDeepDives.map(mapToDeepDive), [filteredDeepDives]);

  const handleCardClick = (deepDive: DeepDive) => {
    navigate(`${APP_ROUTE.DEEP_DIVES}/${deepDive.id}`);
  };

  const handleCtaClick = (deepDive: DeepDive) => {
    if (onScheduleConsultation && deepDive.product_id) {
      onScheduleConsultation(deepDive.product_id, deepDive.name);
    } else {
      navigate(`${APP_ROUTE.DEEP_DIVES}/${deepDive.id}`);
    }
  };

  if (deepDives.length === 0) {
    return null;
  }

  return (
    <Stack spacing={3} sx={containerSx}>
      <Divider />
      <Box sx={contentContainerSx}>
        <Box sx={headerSx}>
          <Typography variant="display-sm-medium">{COACH_PROFILE_STRINGS.deepDives}</Typography>

          <HorizontalMenuTabs
            id="coach-deep-dives-tabs"
            tabs={CATEGORY_TABS.map((tab) => tab.label)}
            value={selectedTabIndex}
            setValue={(value) => setSelectedTabIndex(value)}
            variant="filter"
          />
        </Box>

        <Box sx={gridSx}>
          {mappedDeepDives.map((deepDive) => (
            <Box key={deepDive.id} sx={cardWrapperSx}>
              <DeepDiveCard
                deepDive={deepDive}
                onCardClick={handleCardClick}
                onCtaClick={handleCtaClick}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-4xl'],
  paddingX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingBottom: backrs.spacing['spacing-4xl'],
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'flex-start',
  width: '100%',
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: { xs: backrs.spacing['spacing-2xl'], md: backrs.spacing['spacing-md'] },
  width: '100%',
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-2xl'],
  mt: backrs.spacing['spacing-lg'],
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  padding: backrs.spacing['spacing-md'],
  margin: `calc(-1 * ${backrs.spacing['spacing-md']})`,
  paddingBottom: backrs.spacing['spacing-md'],
  scrollbarWidth: 'thin',
  scrollbarColor: `${backrs.colors['Colors/Border/border-secondary']} transparent`,
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: backrs.colors['Colors/Border/border-secondary'],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: backrs.colors['Colors/Border/border-primary'],
  },
});

const cardWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  width: '300px',
  scrollSnapAlign: 'start',
  '&:last-child': {
    marginRight: backrs.spacing['spacing-2xl'],
  },
});
